import React, { useEffect, useState, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  Box,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  Button,
  Chip,
  Stack,
  Grid,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import clientService from 'src/clientService';
import {
  AcousticIndexEnum,
  ChartRequest,
  ChartResponse,
  LocationAdminDetail,
  Site,
} from 'src/api/Api';
import { useAppState } from 'src/state-management/state-provider';

const acousticIndices = [
  { value: AcousticIndexEnum.Aci, label: 'ACI' },
  { value: AcousticIndexEnum.Bi, label: 'BI' },
  { value: AcousticIndexEnum.Adi, label: 'ADI' },
  { value: AcousticIndexEnum.Aei, label: 'AEI' },
  { value: AcousticIndexEnum.Ndsi, label: 'NDSI' },
];

interface ProjectLevelChartProps {
  projectDeployments: any[];
}

const ProjectLevelChart: React.FC<ProjectLevelChartProps> = ({ projectDeployments }) => {
  const { state } = useAppState();
  const { bioMapData } = state.selectedProject;
  const { bioInteractiveMapFilter } = state;

  const [chartData, setChartData] = useState<ChartResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // Search states
  const [siteSearch, setSiteSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [deploymentSearch, setDeploymentSearch] = useState('');

  // Filter state
  const [selectedSite, setSelectedSite] = useState<string>(
    bioInteractiveMapFilter.selectedSite?.site_id || 'all',
  );
  const [selectedLocation, setSelectedLocation] = useState<string>(
    bioInteractiveMapFilter.selectedLocation?.location_id || '',
  );
  const [selectedDeployments, setSelectedDeployments] = useState<string[]>(
    bioInteractiveMapFilter.selectedDeployment
      ? [bioInteractiveMapFilter.selectedDeployment.deployment_id]
      : [],
  );
  const [selectedIndices] = useState<string[]>([
    AcousticIndexEnum.Aci,
    AcousticIndexEnum.Bi,
    AcousticIndexEnum.Adi,
    AcousticIndexEnum.Aei,
    AcousticIndexEnum.Ndsi,
  ]);
  const [selectedIndexDisplay, setSelectedIndexDisplay] = useState<string>(AcousticIndexEnum.Aci);

  // Filter deployments based on selection
  const filteredDeployments = useMemo(() => {
    if (selectedSite === 'all') {
      return projectDeployments;
    } else if (selectedLocation && selectedLocation !== 'all') {
      return projectDeployments.filter((d) => d.location_id === selectedLocation);
    } else {
      return projectDeployments.filter((d) => d.site_id === selectedSite);
    }
  }, [selectedSite, selectedLocation, projectDeployments]);

  // Available locations based on selected site
  const availableLocations = useMemo(() => {
    if (selectedSite === 'all') {
      return bioMapData?.locations || [];
    }
    return bioMapData?.locations?.filter((l) => l.site_id === selectedSite) || [];
  }, [selectedSite, bioMapData]);

  // Filtered sites based on search
  const filteredSites = useMemo(() => {
    if (!siteSearch) return bioMapData?.sites || [];
    return (
      bioMapData?.sites?.filter((site: Site) =>
        site.site_name.toLowerCase().includes(siteSearch.toLowerCase()),
      ) || []
    );
  }, [siteSearch, bioMapData]);

  // Filtered locations based on search
  const filteredLocations = useMemo(() => {
    if (!locationSearch) return availableLocations;
    return availableLocations.filter((location: LocationAdminDetail) =>
      location.location_name.toLowerCase().includes(locationSearch.toLowerCase()),
    );
  }, [locationSearch, availableLocations]);

  // Filtered deployments based on search
  const searchedDeployments = useMemo(() => {
    if (!deploymentSearch) return filteredDeployments;
    return filteredDeployments.filter((d) =>
      d.deployment_name.toLowerCase().includes(deploymentSearch.toLowerCase()),
    );
  }, [deploymentSearch, filteredDeployments]);

  useEffect(() => {
    if (selectedDeployments.length > 0) {
      fetchChart(selectedDeployments, selectedIndices);
    }
  }, []);

  // Auto-fetch when selection context changes so "All" immediately shows charts
  useEffect(() => {
    const explicit = selectedDeployments;
    if (explicit.length > 0) {
      fetchChart(explicit, selectedIndices);
      return;
    }
    const fallback = filteredDeployments.map((d) => d.deployment_id);
    if (fallback.length > 0) {
      fetchChart(fallback, selectedIndices);
    } else {
      setChartData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite, selectedLocation, selectedDeployments]);

  const fetchChart = async (deployments: string[], indices: string[]) => {
    if (deployments.length === 0) {
      setChartData(null);
      return;
    }

    try {
      setLoading(true);
      const req: ChartRequest = {
        deploymentIds: deployments,
        index: indices.map((idx) => idx as AcousticIndexEnum),
      };
      const res =
        await clientService.api.createDeploymentChartsApiV1DeploymentsBioInsituChartPost(req);
      setChartData(res.data);
    } catch (err) {
      console.error('Error fetching chart:', err);
      setChartData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchWithCurrentSettings = () => {
    const deployments =
      selectedDeployments.length > 0
        ? selectedDeployments
        : filteredDeployments.map((d) => d.deployment_id);
    fetchChart(deployments, selectedIndices);
  };

  const chartOptions = useMemo(() => {
    if (!chartData) return null;
    const series = chartData.comparison.map((c) => {
      const dep = projectDeployments.find((d) => d.deployment_id === c.deployment_id);
      const name = dep?.deployment_name || c.deployment_id;
      return {
        type: 'line' as const,
        name: `${name} (${selectedIndexDisplay.toUpperCase()})`,
        data: (c.indices[selectedIndexDisplay] || []).map((v: number | null | undefined) => v ?? 0),
      };
    });
    return {
      chart: { type: 'line', zoomType: 'x', height: 400 },
      title: { text: 'Project-level Deployment Activity' },
      xAxis: {
        categories: chartData.time_slots,
        labels: { rotation: -45 },
        title: { text: 'Time of Day' },
      },
      yAxis: { title: { text: 'Average of Acoustic Indices' } },
      tooltip: {
        pointFormatter: function (this: Highcharts.Point) {
          return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${(this.y as number).toFixed(2)}</b><br/>`;
        },
      },
      series,
    };
  }, [chartData, selectedIndexDisplay, projectDeployments]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
      {/* Control Panel */}
      <Box sx={{ mb: 3, flexShrink: 0 }}>
        <Grid container spacing={2}>
          {/* Site Selection */}
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography
                variant="caption"
                sx={{ mb: 0.5, fontWeight: 600, color: 'text.secondary', display: 'block' }}
              >
                Site
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedSite}
                  onChange={(e) => {
                    const newSite = e.target.value as string;
                    setSelectedSite(newSite);
                    setSelectedLocation('');
                    // When switching site, default deployments to all under the new scope
                    const deploymentsForSite =
                      newSite === 'all'
                        ? projectDeployments
                        : projectDeployments.filter((d) => d.site_id === newSite);
                    setSelectedDeployments(deploymentsForSite.map((d) => d.deployment_id));
                    setSiteSearch('');
                  }}
                  onClose={() => setSiteSearch('')}
                  MenuProps={{
                    autoFocus: false,
                    disableAutoFocusItem: true,
                    PaperProps: { style: { maxHeight: 300 } },
                  }}
                >
                  <MenuItem
                    value=""
                    disableRipple
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <TextField
                      size="small"
                      placeholder="Search sites..."
                      value={siteSearch}
                      onChange={(e) => {
                        e.stopPropagation();
                        setSiteSearch(e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()} // ✅ Keep focus
                      onKeyDown={(e) => e.stopPropagation()}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </MenuItem>
                  <MenuItem value="all">All Sites</MenuItem>
                  {filteredSites.map((site: Site) => (
                    <MenuItem key={site.site_id} value={site.site_id}>
                      {site.site_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* Location Selection */}
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography
                variant="caption"
                sx={{ mb: 0.5, fontWeight: 600, color: 'text.secondary', display: 'block' }}
              >
                Location
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedLocation}
                  onChange={(e) => {
                    const newLocation = e.target.value as string;
                    setSelectedLocation(newLocation);
                    // Update deployments to all under the new location (or all under current site if none)
                    const deploymentsForScope =
                      newLocation
                        ? projectDeployments.filter((d) => d.location_id === newLocation)
                        : selectedSite === 'all'
                        ? projectDeployments
                        : projectDeployments.filter((d) => d.site_id === selectedSite);
                    setSelectedDeployments(deploymentsForScope.map((d) => d.deployment_id));
                    setLocationSearch('');
                  }}
                  onClose={() => setLocationSearch('')}
                  disabled={availableLocations.length === 0}
                  displayEmpty
                  MenuProps={{
                    autoFocus: false,
                    disableAutoFocusItem: true,
                    PaperProps: {
                      style: { maxHeight: 300 },
                    },
                  }}
                  renderValue={(value) => {
                    if (availableLocations.length === 0) {
                      return <em style={{ color: '#999' }}>No locations available</em>;
                    }
                    if (!value) {
                      return <em style={{ color: '#999' }}>All Locations</em>;
                    }
                    const loc = availableLocations.find(
                      (l: LocationAdminDetail) => l.location_id === value,
                    );
                    return loc?.location_name || value;
                  }}
                >
                  {availableLocations.length > 0 && (
                    <MenuItem
                      value=""
                      disableRipple
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <TextField
                        size="small"
                        placeholder="Search locations..."
                        value={locationSearch}
                        onChange={(e) => {
                          e.stopPropagation();
                          setLocationSearch(e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </MenuItem>
                  )}
                  <MenuItem value="">All Locations</MenuItem>
                  {filteredLocations.map((location: LocationAdminDetail) => (
                    <MenuItem key={location.location_id} value={location.location_id}>
                      {location.location_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* Deployment Selection */}
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography
                variant="caption"
                sx={{ mb: 0.5, fontWeight: 600, color: 'text.secondary', display: 'block' }}
              >
                Deployment
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={selectedDeployments}
                  onChange={(e) => setSelectedDeployments(e.target.value as string[])}
                  onClose={() => setDeploymentSearch('')}
                  renderValue={(selected) => `${selected.length} deployment(s)`}
                  displayEmpty
                  MenuProps={{
                    autoFocus: false,
                    disableAutoFocusItem: true,
                    PaperProps: {
                      style: { maxHeight: 300 },
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    disableRipple
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <TextField
                      size="small"
                      placeholder="Search deployments..."
                      value={deploymentSearch}
                      onChange={(e) => {
                        e.stopPropagation();
                        setDeploymentSearch(e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </MenuItem>
                  {searchedDeployments.map((d) => {
                    const location = availableLocations.find(
                      (l) => l.location_id === d.location_id,
                    );
                    const site = bioMapData?.sites?.find((s) => s.site_id === d.site_id);
                    return (
                      <MenuItem key={d.deployment_id} value={d.deployment_id}>
                        <Checkbox checked={selectedDeployments.includes(d.deployment_id)} />
                        <ListItemText
                          primary={d.deployment_name}
                          secondary={`${site?.site_name || ''} - ${location?.location_name || ''}`}
                        />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* Display Index Selection */}
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0,
                  p: 0,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ mb: 0, fontWeight: 600, color: 'text.secondary', display: 'block' }}
                >
                  Display Index
                </Typography>
                <Tooltip
                  sx={{ mb: -0.5 }}
                  title={
                    <Box>
                      <Typography variant="body2">
                        <b>ACI</b> – Acoustic Complexity Index
                      </Typography>
                      <Typography variant="body2">
                        <b>BI</b> – Bioacoustic Index
                      </Typography>
                      <Typography variant="body2">
                        <b>ADI</b> – Acoustic Diversity Index
                      </Typography>
                      <Typography variant="body2">
                        <b>AEI</b> – Acoustic Evenness Index
                      </Typography>
                      <Typography variant="body2">
                        <b>NDSI</b> – Normalized Difference Soundscape Index
                      </Typography>
                    </Box>
                  }
                  placement="left"
                  arrow
                >
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedIndexDisplay}
                  onChange={(e) => setSelectedIndexDisplay(e.target.value as string)}
                >
                  {selectedIndices.map((idx) => {
                    const index = acousticIndices.find((i) => i.value === idx);
                    return (
                      <MenuItem key={idx} value={idx}>
                        {index?.label || idx}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>

        {/* Selected Indices Display */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="caption" sx={{ mb: 1, fontWeight: 600, display: 'block' }}>
              Selected Acoustic Indices:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {selectedIndices.map((idx) => {
                const index = acousticIndices.find((i) => i.value === idx);
                return (
                  <Chip
                    key={idx}
                    label={index?.label || idx}
                    size="small"
                    color={idx === selectedIndexDisplay ? 'primary' : 'default'}
                    onClick={() => setSelectedIndexDisplay(idx)}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Stack>
          </Box>
          {/* Action Button - Positioned below chart */}

          <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'right', p: 1 }}>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={handleFetchWithCurrentSettings}
              disabled={selectedDeployments.length === 0 && filteredDeployments.length === 0}
              size="small"
              sx={{
                px: 4,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Add to Chart
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Chart Display */}
      <Box
        sx={{
          flex: 1,
          minHeight: '400px',
          mb: 2,
          bgcolor: 'background.paper',
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={30} />
              <Typography variant="body2">Fetching chart data...</Typography>
            </Stack>
          </Box>
        ) : chartOptions ? (
          <Box sx={{ width: '100%', height: '100%' }}>
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
              containerProps={{ style: { height: '100%', width: '100%' } }}
            />
          </Box>
        ) : (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
          >
            <Typography color="textSecondary">
              No chart data available. Configure filters and click the button to fetch analytics.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProjectLevelChart;
